import axios from 'axios';

/**
 * Get enabled platforms for a specific module
 * @param {string} moduleKey - The module key (e.g., 'createPost', 'schedulePost', 'knackMoments')
 * @returns {Promise<Array>} Array of enabled platform objects
 */
export const getEnabledPlatforms = async (moduleKey) => {
  try {
    const UserID = localStorage.getItem('UserID');
    if (!UserID) return [];

    // Map module keys to database module names
    const moduleNameMap = {
      'schedulePost': 'schedule_post',
      'knackMoments': 'knack_moments',
      'knackFeeds': 'knack_feeds',
      'knackChallenges': 'knack_challenges',
      'createPost': 'create_post'
    };

    const dbModuleName = moduleNameMap[moduleKey] || moduleKey;

    // Fetch platform settings for this module
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/all/${UserID}`
    );

    if (response.data.success && response.data.settings) {
      const settings = response.data.settings;
      
      // Filter settings for this module
      const moduleSettings = settings.filter(s => 
        s.module_name && s.module_name.toLowerCase() === dbModuleName.toLowerCase()
      );

      // Check if module is enabled
      if (moduleSettings.length === 0) return [];
      
      const moduleEnabled = moduleSettings[0].module_setting_status === 1 || 
                           moduleSettings[0].module_setting_status === true;
      
      if (!moduleEnabled) return [];

      // Get enabled platforms
      const enabledPlatforms = [];
      const AccountType = localStorage.getItem('AccountType');
      
      moduleSettings.forEach(setting => {
        const platformEnabled = setting.platform_status === 1 || setting.platform_status === true;
        if (platformEnabled) {
          const platformCode = setting.platform_name?.toLowerCase();
          
          // Filter out Facebook if AccountType is not Page
          if (platformCode === 'facebook' && AccountType !== 'Page' && AccountType !== 'page') {
            return; // Skip Facebook for non-Page accounts
          }
          
          enabledPlatforms.push({
            name: setting.platform_name,
            code: platformCode
          });
        }
      });

      return enabledPlatforms;
    }

    return [];
  } catch (error) {
    console.error('Error fetching enabled platforms:', error);
    // Return default platforms if API fails (filter Facebook if AccountType is not Page)
    const AccountType = localStorage.getItem('AccountType');
    const defaultPlatforms = [
      { name: 'LinkedIn', code: 'linkedin' }
    ];
    
    if (AccountType === 'Page' || AccountType === 'page') {
      defaultPlatforms.push({ name: 'Facebook', code: 'facebook' });
    }
    
    return defaultPlatforms;
  }
};

/**
 * Format platform names for buttons
 * @param {Array} platforms - Array of platform objects with name property
 * @returns {string} Formatted platform names string
 */
export const formatPlatformNamesForButton = (platforms) => {
  if (!platforms || platforms.length === 0) {
    return 'Select Platform';
  }

  if (platforms.length === 1) {
    return `Post to ${platforms[0].name}`;
  }

  if (platforms.length === 2) {
    return `Post to ${platforms[0].name} & ${platforms[1].name}`;
  }

  // For 3+ platforms, use "Post to LinkedIn, Facebook & Instagram"
  const names = platforms.map(p => p.name);
  const lastPlatform = names.pop();
  return `Post to ${names.join(', ')} & ${lastPlatform}`;
};

/**
 * Format platform names for success alerts
 * @param {Array} platforms - Array of platform objects with name property
 * @returns {string} Formatted platform names string
 */
export const formatPlatformNamesForAlert = (platforms) => {
  if (!platforms || platforms.length === 0) {
    return 'Posted successfully';
  }

  if (platforms.length === 1) {
    return `Posted successfully to ${platforms[0].name}`;
  }

  if (platforms.length === 2) {
    return `Posted successfully to ${platforms[0].name} and ${platforms[1].name}`;
  }

  // For 3+ platforms
  const names = platforms.map(p => p.name);
  const lastPlatform = names.pop();
  return `Posted successfully to ${names.join(', ')} and ${lastPlatform}`;
};

/**
 * Get enabled platforms synchronously from localStorage or context
 * This is a fallback if async call fails
 * @param {string} moduleKey - The module key
 * @returns {Array} Array of enabled platform objects
 */
export const getEnabledPlatformsSync = (moduleKey) => {
  // Try to get from localStorage if available
  try {
    const cachedSettings = localStorage.getItem(`platformSettings_${moduleKey}`);
    if (cachedSettings) {
      const settings = JSON.parse(cachedSettings);
      return settings.enabledPlatforms || [];
    }
  } catch (error) {
    console.error('Error reading cached platform settings:', error);
  }

  // Default fallback (filter Facebook if AccountType is not Page)
  const AccountType = localStorage.getItem('AccountType');
  const defaultPlatforms = [
    { name: 'LinkedIn', code: 'linkedin' }
  ];
  
  if (AccountType === 'Page' || AccountType === 'page') {
    defaultPlatforms.push({ name: 'Facebook', code: 'facebook' });
  }
  
  return defaultPlatforms;
};

/**
 * Get all active platforms from SocialMediaPlatforms table
 * This returns all platforms regardless of Platform Settings
 * @returns {Promise<Array>} Array of active platform objects
 */
export const getAllActivePlatforms = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/platforms`
    );

    if (response.data.success && response.data.platforms) {
      // Map the response to match the expected format
      return response.data.platforms.map(platform => ({
        name: platform.name,
        code: platform.code
      }));
    }

    // Fallback to default platforms if API fails
    return [
      { name: 'LinkedIn', code: 'linkedin' },
      { name: 'Facebook', code: 'facebook' },
      { name: 'Instagram', code: 'instagram' }
    ];
  } catch (error) {
    console.error('Error fetching all active platforms:', error);
    // Return default platforms as fallback
    return [
      { name: 'LinkedIn', code: 'linkedin' },
      { name: 'Facebook', code: 'facebook' },
      { name: 'Instagram', code: 'instagram' }
    ];
  }
};

// Note: React hook should be in a separate file if needed
// This utility file focuses on pure functions

